import * as React from 'react'
import { EmailField, HashFields, Label, PasswordField } from 'react-rails-form-helpers'
import { ICrashdownGlobals } from '../../CrashdownTypeStubs'

declare var crashdown: ICrashdownGlobals
declare var Routes: any

const PASSWORD_MIN_LENGTH = 6

// Single source of truth for the account field-name segments. The collapsed
// hidden mirror (`renderCollapsedAccountFields`) builds its names from these via
// `accountFieldName`, so renaming a segment flows to both the live inputs and
// the mirror — there is otherwise no compile-time link, and a desync is the
// silent submit-payload bug CD-167 exists to fix.
export const CUSTOMER_FIELD_NAMESPACE = 'customer'

export const ACCOUNT_FIELD_NAMES = {
  email: 'email',
  password: 'password',
} as const

// Matches how react-rails-form-helpers serializes the nested live inputs:
// `<FormFor name>[customer][email]`.
export const accountFieldName = (
  formName: string,
  field: keyof typeof ACCOUNT_FIELD_NAMES,
): string => `${formName}[${CUSTOMER_FIELD_NAMESPACE}][${ACCOUNT_FIELD_NAMES[field]}]`

// Mirrors the User model's email format (app/models/user.rb). Ruby's `\A`/`\Z`
// anchors become `^`/`$` — jsdom and browsers don't support `\A`/`\Z`.
const EMAIL_FORMAT = /^([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})$/i

const EMAIL_INVALID_MESSAGE = 'Please enter a valid email address.'
const PASSWORD_TOO_SHORT_MESSAGE = `Password must be at least ${PASSWORD_MIN_LENGTH} characters long.`

interface IProps {
  // Controlled values owned by the shell so they survive this step unmounting
  // (CD-167); the password lives in a transient shell field kept out of the
  // serialized checkout/cashier payload.
  email: string
  password: string
  // Authenticated visitor: the fields are locked and the "log in" prompt is
  // suppressed, but Step 1 still renders since the email is part of the payload.
  signedIn: boolean
  onEmailChange: (value: string) => void
  onPasswordChange: (value: string) => void
  onContinue: () => void
}

interface IState {
  // Display-only feedback, local to the active step; not hoisted, and may reset
  // on collapse. Only the field values must persist, and those live in the shell.
  emailError?: string
  passwordError?: string
  emailAlreadyExists: boolean
}

const EMAIL_ERROR_ID = 'step1-email-error'
const PASSWORD_ERROR_ID = 'step1-password-error'

export class Step1Account extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    this.state = {
      emailError: undefined,
      passwordError: undefined,
      emailAlreadyExists: false,
    }
  }

  public handleEmailChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.props.onEmailChange(event.currentTarget.value)
    this.setState({ emailAlreadyExists: false })
  }

  // Mirrors the control `CustomerForm`: on blur, ask the back end whether the
  // email is already registered and, if so, swap to a "log in" prompt.
  public checkEmailUniqueness = (event: React.FocusEvent<HTMLInputElement>) => {
    const { currentTarget } = event
    const { value: email } = currentTarget

    if (!currentTarget.checkValidity()) {
      this.setState({ emailAlreadyExists: false })
      return
    }

    fetch(
      Routes.igniter_account_uniqueness_check_path({ email }),
      { credentials: 'same-origin' },
    ).then((response) => {
      if (response.ok) {
        // The email is taken: switch to "log in" mode, drop any stale field
        // errors, and clear the now-disabled password value, since the path
        // forward is Login, not Continue re-validation.
        this.setState({ emailAlreadyExists: true, emailError: undefined, passwordError: undefined })
        this.props.onPasswordChange('')
      } else {
        // checkValidity() is looser than EMAIL_FORMAT (`a@b` passes it), so
        // re-validate rather than blind-clear. passwordError is left untouched:
        // an email blur must not clear a real password error (CD-193).
        this.setState({ emailAlreadyExists: false, emailError: this.validateEmail(email) })
      }
    })
  }

  public handlePasswordChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.props.onPasswordChange(event.currentTarget.value)
  }

  public showLoginModal = (event: React.SyntheticEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault()
    crashdown.setLoginModalEmail(this.props.email)
    crashdown.user_session.triggerLoginModal()
  }

  public handleContinue = () => {
    // The signed-in password input renders a mask, not a real value, so the
    // length rule would always fail it.
    if (this.props.signedIn) {
      this.props.onContinue()
      return
    }

    const emailError = this.validateEmail(this.props.email)
    const passwordError = this.validatePassword(this.props.password)

    this.setState({ emailError, passwordError })

    if (!emailError && !passwordError) {
      this.props.onContinue()
    }
  }

  public render() {
    const { email, password, signedIn } = this.props
    const { emailError, passwordError, emailAlreadyExists } = this.state

    return (
      <div className="step1-account" data-testid="step1-account">
        <section className="account-form">
          <HashFields name={CUSTOMER_FIELD_NAMESPACE}>
            <Label htmlFor="email">Email</Label>
            <EmailField
              name={ACCOUNT_FIELD_NAMES.email}
              autoComplete="email"
              required={true}
              value={email}
              disabled={signedIn}
              onChange={this.handleEmailChange}
              onBlur={this.checkEmailUniqueness}
              aria-invalid={emailError ? true : undefined}
              aria-describedby={emailError ? EMAIL_ERROR_ID : undefined}
            />
            {this.renderFieldError('email', emailError)}
            {!signedIn && this.renderEmailBottomLabel()}

            <Label htmlFor="password">Password</Label>
            {/* Controlled (CD-167): a completed step unmounts on collapse, which
                would lose a DOM-owned password value. The shell holds it in a
                transient field kept out of the serialized payload, so it is no
                more exposed than the earlier uncontrolled input. */}
            <PasswordField
              name={ACCOUNT_FIELD_NAMES.password}
              autoComplete="new-password"
              required={true}
              value={signedIn ? '•••••••••••' : password}
              disabled={signedIn || emailAlreadyExists}
              onChange={this.handlePasswordChange}
              aria-invalid={passwordError ? true : undefined}
              aria-describedby={passwordError ? PASSWORD_ERROR_ID : undefined}
            />
            {this.renderFieldError('password', passwordError)}
          </HashFields>
        </section>

        {emailAlreadyExists ?
          <button
            type="button"
            className="button trial-redesign__continue step1-account__continue"
            data-testid="step1-continue"
            onClick={this.showLoginModal}
          >
            Login
          </button> :
          <button
            type="button"
            className="button trial-redesign__continue step1-account__continue"
            data-testid="step1-continue"
            onClick={this.handleContinue}
          >
            Continue to Billing Information
          </button>
        }
      </div>
    )
  }

  private renderEmailBottomLabel() {
    const { emailAlreadyExists } = this.state

    if (emailAlreadyExists) {
      return (
        <Label className="field-bottom-label" htmlFor="email">
          An account with this email address already exists.{' '}
          <a href="#" onClick={this.showLoginModal}>Please log in.</a>
        </Label>
      )
    }

    return (
      <Label className="field-bottom-label" htmlFor="email">
        Already have an account?{' '}
        <a href="#" tabIndex={-1} onClick={this.showLoginModal}>Click here to log in.</a>
      </Label>
    )
  }

  private renderFieldError(field: 'email' | 'password', message?: string) {
    if (!message) {
      return null
    }

    const id = field === 'email' ? EMAIL_ERROR_ID : PASSWORD_ERROR_ID

    return (
      <span id={id} className="field-error" data-testid={`step1-${field}-error`} role="alert">
        {message}
      </span>
    )
  }

  private validateEmail(email: string): string | undefined {
    return EMAIL_FORMAT.test(email) ? undefined : EMAIL_INVALID_MESSAGE
  }

  private validatePassword(password: string): string | undefined {
    return password.length >= PASSWORD_MIN_LENGTH ? undefined : PASSWORD_TOO_SHORT_MESSAGE
  }
}
